import { useState, useEffect, useRef, useCallback } from 'react'
import { useSearchParams, useNavigate, Link } from 'react-router-dom'
import { apiClient } from '../services/api'
import { useAuth } from '../hooks/useAuth'
import { LoadingSpinner } from '../components/ui/LoadingSpinner'
import { MailCheck, CheckCircle2, XCircle, Mail } from 'lucide-react'
import toast from 'react-hot-toast'

type Status = 'idle' | 'verifying' | 'success' | 'error' | 'rate_limited'

// apiClient.request throws the response body ({ error: { code } }), not the
// axios error.
const errorCode = (err: any): string | undefined => err?.error?.code ?? err?.response?.data?.error?.code

export function VerifyEmailPage() {
  const [params] = useSearchParams()
  const navigate = useNavigate()
  const { user, refreshUser, logout } = useAuth()
  const token = params.get('token')
  const [status, setStatus] = useState<Status>(token ? 'verifying' : 'idle')
  const [resending, setResending] = useState(false)
  const ran = useRef(false)

  const verify = useCallback(async (value: string) => {
    setStatus('verifying')
    try {
      await apiClient.verifyEmail(value)
      setStatus('success')
      // If the user is logged in, refresh so the email-verified gate lifts.
      await refreshUser()
    } catch (err: any) {
      // A rate-limited request says nothing about the link; don't call it expired.
      setStatus(errorCode(err) === 'RATE_LIMIT' ? 'rate_limited' : 'error')
    }
  }, [refreshUser])

  useEffect(() => {
    if (!token || ran.current) return
    ran.current = true
    void verify(token)
  }, [token, verify])

  const handleResend = async () => {
    setResending(true)
    try {
      await apiClient.resendVerification()
      toast.success('Verification email sent — check your inbox.')
    } catch (err: any) {
      toast.error(
        errorCode(err) === 'RESEND_RATE_LIMIT'
          ? 'Please wait a minute before requesting another email.'
          : 'Could not send the email. Try again shortly.',
      )
    } finally {
      setResending(false)
    }
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50 px-4">
      <div className="max-w-md w-full bg-white rounded-lg shadow p-8 text-center">
        {status === 'verifying' && (
          <>
            <LoadingSpinner size="lg" />
            <h1 className="mt-4 text-xl font-bold text-gray-900">Verifying your email…</h1>
          </>
        )}

        {status === 'success' && (
          <>
            <CheckCircle2 className="h-12 w-12 text-green-600 mx-auto" />
            <h1 className="mt-4 text-xl font-bold text-gray-900">Email verified</h1>
            <p className="mt-2 text-gray-600">Your account is active. You're all set.</p>
            <button
              onClick={() => navigate(user ? '/dashboard' : '/login')}
              className="mt-6 w-full bg-blue-600 text-white py-2 rounded-md font-medium hover:bg-blue-700"
            >
              {user ? 'Go to dashboard' : 'Log in'}
            </button>
          </>
        )}

        {status === 'rate_limited' && token && (
          <>
            <XCircle className="h-12 w-12 text-amber-500 mx-auto" />
            <h1 className="mt-4 text-xl font-bold text-gray-900">Too many attempts</h1>
            <p className="mt-2 text-gray-600">
              Your link may still be valid. Wait a minute, then try again.
            </p>
            <button
              onClick={() => void verify(token)}
              className="mt-6 w-full bg-blue-600 text-white py-2 rounded-md font-medium hover:bg-blue-700"
            >
              Try again
            </button>
          </>
        )}

        {status === 'error' && (
          <>
            <XCircle className="h-12 w-12 text-red-600 mx-auto" />
            <h1 className="mt-4 text-xl font-bold text-gray-900">Link expired or invalid</h1>
            <p className="mt-2 text-gray-600">
              This verification link is no longer valid. Request a fresh one below.
            </p>
            {user ? (
              <button
                onClick={handleResend}
                disabled={resending}
                className="mt-6 w-full bg-blue-600 text-white py-2 rounded-md font-medium hover:bg-blue-700 disabled:opacity-60"
              >
                {resending ? 'Sending…' : 'Resend verification email'}
              </button>
            ) : (
              <Link
                to="/login"
                className="mt-6 inline-block w-full bg-blue-600 text-white py-2 rounded-md font-medium hover:bg-blue-700"
              >
                Log in to resend
              </Link>
            )}
          </>
        )}

        {status === 'idle' && (
          <>
            <MailCheck className="h-12 w-12 text-blue-600 mx-auto" />
            <h1 className="mt-4 text-xl font-bold text-gray-900">Verify your email</h1>
            <p className="mt-2 text-gray-600">
              We sent a verification link to{' '}
              <span className="font-medium text-gray-900">{user?.email || 'your email'}</span>.
              Click it to activate your account.
            </p>
            <button
              onClick={handleResend}
              disabled={resending || !user}
              className="mt-6 w-full inline-flex items-center justify-center gap-2 bg-blue-600 text-white py-2 rounded-md font-medium hover:bg-blue-700 disabled:opacity-60"
            >
              <Mail className="h-4 w-4" />
              {resending ? 'Sending…' : 'Resend verification email'}
            </button>
            {user && (
              <button
                onClick={() => logout()}
                className="mt-3 w-full text-sm text-gray-500 hover:text-gray-700"
              >
                Sign out
              </button>
            )}
          </>
        )}
      </div>
    </div>
  )
}
